import { parsePhoneNumberFromString } from 'libphonenumber-js';

declare let hj: any;

export const getVideoUrl = (value: string) => {
  const regexYoutube =
    /^(?:http(?:s)?:\/\/)?(?:www\.)?(?:m\.)?(?:youtu\.be\/|youtube\.com\/(?:(?:watch)?\?(?:.*&)?v(?:i)?=|(?:embed|v|vi|user)\/))([^?&"""'>]+)/;
  const regexVimeo = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/;

  if (regexYoutube.test(value)) {
    return 'https://www.youtube.com/embed/' + value.match(regexYoutube)[1];
  }

  if (regexVimeo.test(value)) {
    const parseUrl = regexVimeo.exec(value);

    const id = parseUrl[5];
    return 'https://player.vimeo.com/video/' + id + '?title=0&byline=0&portrait=0';
  }

  return value;
};

export const hjTagging = (tag: string) => {
  if (typeof hj !== 'undefined') {
    hj('tagRecording', [tag]);
  }
};

export const stripTags = (content: string) => {
  if (typeof content !== 'undefined') {
    return content
      .replace(/<style([\S\s]*?)>([\S\s]*?)<\/style>/gi, '')
      .replace(/<script([\S\s]*?)>([\S\s]*?)<\/script>/gi, '')
      .replace(/( {2}|<([^>]+)>)/gi, '');
  }

  return '';
};

export const replaceAll = (text: string, search: string, replace: string) => {
  return text.split(search).join(replace);
};

const BR_COUNTRY_CODE = '55';

const brNationalNormalize = (digits: string): string => {
  let national = digits;

  if ((national.length === 12 || national.length === 13) && national.startsWith(BR_COUNTRY_CODE)) {
    national = national.slice(2);
  }

  if (national.startsWith('0')) {
    national = national.slice(1);
  }

  if (national.length === 10 && /[6-9]/.test(national[2])) {
    national = national.slice(0, 2) + '9' + national.slice(2);
  }

  return national;
};

/**
 * Normaliza um telefone para E.164 sem "+" (pronto p/ `wa.me`). Com "+"/"00" usa o DDI informado;
 * sem indicação de país, assume BR. Retorna { status, value } - value é o número ou a mensagem de erro.
 */
export const whatsappNormalize = (phone: string): { status: 'error' | 'success'; value: string } => {
  if (!phone || phone.trim().length === 0) {
    return {
      status: 'error',
      value: 'Celular não informado'
    };
  }

  const raw = phone.trim();
  const international = raw.startsWith('00') ? '+' + raw.slice(2) : raw;

  const candidate = international.startsWith('+')
    ? international
    : '+' + BR_COUNTRY_CODE + brNationalNormalize(international.replace(/\D/g, ''));

  const parsed = parsePhoneNumberFromString(candidate);

  if (parsed && parsed.isPossible()) {
    return {
      status: 'success',
      value: parsed.number.slice(1)
    };
  }

  return {
    status: 'error',
    value: 'Número de WhatsApp inválido'
  };
};

export const firstAndLastName = (fullName: string) => {
  const nameParts = fullName.trim().split(' ');
  const countParts = nameParts.length;

  if (countParts === 1) {
    return fullName.trim();
  }

  const firstName = nameParts[0];
  const lastName = nameParts[countParts - 1];
  return firstName + ' ' + lastName;
};

export const phoneMask = (phone: string) => {
  const phoneArray = phone.replace(/(\D)+/g, '').split('');

  const nineDigitsMask = (phone: string[]) => {
    phone.splice(0, 0, ' (');
    phone.splice(3, 0, ') ');
    phone.splice(9, 0, '-');
    return phone.join('');
  };

  const eightDigitsMask = (phone: string[]) => {
    phone.splice(0, 0, ' (');
    phone.splice(3, 0, ') ');
    phone.splice(8, 0, '-');
    return phone.join('');
  };

  const extraDigitsMask = (phone: string[]) => {
    phone.splice(0, 0, '+');
    return phone.join('');
  };

  if (phoneArray.length === 11) {
    return nineDigitsMask(phoneArray);
  }

  if (phoneArray.length === 10) {
    return eightDigitsMask(phoneArray);
  }

  if (phoneArray.length > 11) {
    const nineDigits = phoneArray.slice(phoneArray.length - 11);
    const extraDigits = phoneArray.slice(0, phoneArray.length - 11);
    return extraDigitsMask(extraDigits) + nineDigitsMask(nineDigits);
  }

  return phone;
};

export const cleanString = (str: string) => {
  if (!str) return;
  return str
    .replace(/[{()}.-/_/]/g, '')
    .replace(/\s/g, '')
    .split('-')
    .join('')
    .split('+')
    .join('');
};

export const docMask = (document: string) => {
  const docArray = document.replace(/(\D)+/g, '').split('');

  const cpfMask = (doc: string[]) => {
    doc.splice(3, 0, '.');
    doc.splice(7, 0, '.');
    doc.splice(11, 0, '-');
    return doc.join('');
  };

  const cnpjMask = (doc: string[]) => {
    doc.splice(2, 0, '.');
    doc.splice(6, 0, '.');
    doc.splice(10, 0, '/');
    doc.splice(15, 0, '-');
    return doc.join('');
  };

  if (docArray.length === 11) {
    return cpfMask(docArray);
  }

  if (docArray.length === 13 || docArray.length === 14) {
    return cnpjMask(docArray);
  }

  return document;
};

export const formatDateTimeFromPtBr = (dateTimePtBr: string) => {
  const dateSplitted = dateTimePtBr.split(' ');
  const strData = dateSplitted[0];
  const strHour = dateSplitted[1];

  const dateParts = strData.split('/');
  const hourParts = strHour.split(':');

  const year = parseInt(dateParts[2]);
  const month = parseInt(dateParts[1]) - 1;
  const day = parseInt(dateParts[0]);

  const hours = parseInt(hourParts[0] ? hourParts[0] : '0');
  const minutes = parseInt(hourParts[1] ? hourParts[1] : '0');
  const seconds = parseInt(hourParts[2] ? hourParts[2] : '0');

  return new Date(year, month, day, hours, minutes, seconds);
};

export const dateNormalizeIso8601Format = (date: any): Date => {
  if (typeof date == 'string') {
    date = new Date(date.replace(' ', 'T'));
  }

  return date;
};

export const convertDateToHumanPtBr = (date: any) => {
  date = dateNormalizeIso8601Format(date);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();

  const strDay = day < 10 ? '0' + day : day;
  const strMonth = month < 10 ? '0' + month : month;
  const strHours = hours < 10 ? '0' + hours : hours;
  const strMinutes = minutes < 10 ? '0' + minutes : minutes;

  return `${strDay}/${strMonth}/${year} às ${strHours}:${strMinutes}`;
};

export const mountLocation = (event: any) => {
  let location = '';

  if (!!event.street && event.street.length > 0) {
    location += !!location && location.length > 0 ? ' - ' : '';
    location += event.street;
  }

  if (!!event.number && event.number.length > 0) {
    location += !!location && location.length > 0 ? ', ' : '';
    location += event.number;
  }

  if (!!event.complement && event.complement.length > 0) {
    location += ' (' + event.complement + ')';
  }

  if (!!event.district && event.district.length > 0) {
    location += !!location && location.length > 0 ? ' - ' : '';
    location += event.district;
  }

  if (!!event.zip && event.zip.length > 0) {
    location += !!location && location.length > 0 ? ' - ' : '';
    location += 'CEP: ' + event.zip;
  }

  if (!!event.city && event.city.length > 0) {
    location += !!location && location.length > 0 ? ' - ' : '';
    location += event.city;
  }

  if (!!event.state && event.state.length > 0) {
    location += !!location && location.length > 0 ? ' - ' : '';
    location += event.state;
  }

  return location.trim();
};

export const isValidChar = (char: string) => {
  const regexValidUrl = /^[A-Za-z0-9_-]+$/;
  return regexValidUrl.test(char);
};

export const normalizeEventUrl = (eventTitle: string) => {
  const formattedUrl = eventTitle
    .replace(/ /g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  let finalValue = '';

  for (let i = 0; i < formattedUrl.length; i++) {
    const valid = isValidChar(formattedUrl.charAt(i));
    finalValue += valid ? formattedUrl.charAt(i) : '';
  }

  return finalValue;
};

// funções para estilos

export const fontSizePxToRem = (size: number) => `${size / 16}rem`;

export const setPadding = (
  top: number | string,
  right: number | string,
  bottom: number | string,
  left: number | string
) => {
  const paddingTop = top === 0 || top ? { paddingTop: top } : {};
  const paddingRight = right === 0 || right ? { paddingRight: right } : {};
  const paddingBottom = bottom === 0 || bottom ? { paddingBottom: bottom } : {};
  const paddingLeft = left === 0 || left ? { paddingLeft: left } : {};

  return {
    ...paddingTop,
    ...paddingRight,
    ...paddingBottom,
    ...paddingLeft
  };
};

export const capitalizeFirstLetter = (str: string) => {
  return str[0].toUpperCase() + str.slice(1);
};

export const setMargin = (
  top: number | string,
  right: number | string,
  bottom: number | string,
  left: number | string
) => {
  const marginTop = top === 0 || top ? { marginTop: top } : {};
  const marginRight = right === 0 || right ? { marginRight: right } : {};
  const marginBottom = bottom === 0 || bottom ? { marginBottom: bottom } : {};
  const marginLeft = left === 0 || left ? { marginLeft: left } : {};

  return {
    ...marginTop,
    ...marginRight,
    ...marginBottom,
    ...marginLeft
  };
};

export const formatUrlWithUtm = (destiny: string, source: string, medium: string) => {
  const urlHasParams = destiny.includes('?');
  const urlParamsSeparator = urlHasParams ? '&' : '?';

  const utmParams = `utm_source=${source}&utm_medium=${medium}`;

  return destiny + urlParamsSeparator + utmParams;
};

export const strTimeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};
